/**
 * The Intel extras (dashboard spec 2026-10-09 §2.2 tab 6, W4 card C5): Intel › Markets, Stations and Charts, after
 * M1's Wire (10) and Charter (20). Read-only bodies over `model.ts`; each is a list of buttons (one row per station
 * or system) whose detail opens in place, moved with the shell's keys through `useButtonNav` like the Charter Board.
 * Selling charts is docked-only: a `DockedAction` with the nearest chart buyer and Set course (spec §2.1). Each body
 * re-reads at `refreshHz` (4 Hz) only while the dashboard is open; the shell keeps it mounted while closed.
 */
import { useEffect, useRef, useState, type ReactElement } from 'react'
import type { Career } from '../../../../career/types'
import { StationPanel } from '../../../strategic/StationPanel'
import { useButtonNav } from '../../../strategic/dashNav'
import type { DashboardConfig } from '../../config'
import { dockedActionModel, toNearestStation } from '../../data'
import type { StationDirectory } from '../../data/types'
import { AgeStamp, DockedAction } from '../../parts'
import type { DashSection, DashSectionProps } from '../../registry'
import { loadIntelConfig, type IntelConfig } from './config'
import { chartsModel, marketsModel, stationsModel, type IntelBase, type MarketEntry, type SeenView, type ViewStations } from './model'
import './intel.css'

export interface IntelDeps {
  readonly dash: DashboardConfig
  career(): Career | null
  /** This system's live strategic view (null outside a system). */
  here(): ViewStations | null
  /** The strategic id of the docked station (null in flight). */
  dockedStationId(): string | null
  /** Other systems' views seen this session (memory only). */
  seen(): ReadonlyMap<string, SeenView>
  /** `ReachState.charts` (null outside the Reach). */
  charts(): { readonly unsold: Readonly<Record<string, number>>; readonly firstDiscoveries: readonly string[] } | null
  /** `explore.json charts.firstDiscoveryMult`. */
  readonly firstMult: number
  /** The known-station directory (null outside the Reach). */
  directory(): StationDirectory | null
  setCourse(dir: StationDirectory, stationId: string): void
  systemName(id: string): string
  isOpen(): boolean
}

/** Re-renders at `hz` while the dashboard is open. */
function useTick(hz: number, isOpen: () => boolean): void {
  const [, setN] = useState(0)
  useEffect(() => {
    const id = window.setInterval(() => {
      if (isOpen()) setN((n) => (n + 1) % 1_000_000)
    }, 1000 / hz)
    return (): void => window.clearInterval(id)
  }, [hz, isOpen])
}

function Empty({ text }: { text: string }): ReactElement {
  return <p className="dash-empty__line">{text}</p>
}

/** One list row: a button that opens its detail in place. */
function RowButton(p: { title: string; sub: string; open: boolean; onToggle(): void; children: ReactElement }): ReactElement {
  return (
    <button type="button" className="intel-row__head" aria-expanded={p.open} onClick={p.onToggle}>
      <span className="intel-row__title">{p.title}</span>
      <span className="intel-row__sub">{p.sub}</span>
      {p.children}
    </button>
  )
}

function WareTable({ cfg, row }: { cfg: IntelConfig; row: MarketEntry }): ReactElement {
  const t = cfg.markets
  return (
    <table className="intel-table" data-testid="intel-ware-table">
      <thead>
        <tr>
          <th>{t.ware}</th>
          <th>{t.buy}</th>
          <th>{t.sell}</th>
          {row.live ? <th>{t.stock}</th> : null}
        </tr>
      </thead>
      <tbody>
        {row.wares.map((w) => (
          <tr key={w.ware} data-ware={w.ware}>
            <td>
              {w.name}
              {w.chip !== null ? (
                <span className={`intel-chip intel-chip--${w.chip.tone}`} title={w.chip.tooltip} data-cause={w.chip.tag}>
                  {w.chip.label} {w.chip.tagLabel}
                </span>
              ) : null}
            </td>
            <td className="intel-num">
              {w.buy}
              {w.buyAt !== null ? <span className="intel-dim"> {w.buyAt}</span> : null}
            </td>
            <td className="intel-num">
              {w.sell}
              {w.sellAt !== null ? <span className="intel-dim"> {w.sellAt}</span> : null}
              {w.trend !== null ? (
                <span className={`intel-trend intel-trend--${w.trend.tone}`} title={w.trend.label}>
                  {' '}
                  {w.trend.glyph}
                </span>
              ) : null}
            </td>
            {row.live ? (
              <td className={`intel-num intel-stock--${w.stock?.tone ?? 'ok'}`} title={w.stock?.label}>
                {w.stock?.detail ?? t.none}
              </td>
            ) : null}
          </tr>
        ))}
      </tbody>
    </table>
  )
}

export function createIntelSections(deps: IntelDeps): DashSection[] {
  const cfg = loadIntelConfig()
  const hz = deps.dash.refreshHz
  const isOpen = deps.isOpen

  const base = (career: Career): IntelBase => ({ cfg, age: deps.dash.age, now: career.gameTime, systemName: deps.systemName })

  /** The open row (one at a time); a click on the open row closes it. */
  function useOpenRow(): [string | null, (key: string) => void] {
    const [open, setOpen] = useState<string | null>(null)
    return [open, (key: string): void => setOpen((o) => (o === key ? null : key))]
  }

  function Markets({ nav }: DashSectionProps): ReactElement {
    useTick(hz, isOpen)
    const root = useRef<HTMLDivElement>(null)
    useButtonNav(nav, root)
    const [open, toggle] = useOpenRow()
    const career = deps.career()
    if (career === null) return <Empty text={cfg.noCareer} />
    const m = marketsModel({ ...base(career), here: deps.here(), dockedStationId: deps.dockedStationId(), priceLog: career.priceLog, named: deps.seen() })
    if (m.rows.length === 0) return <Empty text={cfg.markets.empty} />
    return (
      <div ref={root} className="intel" data-intel="markets">
        <ul className="intel-list">
          {m.rows.map((r) => (
            <li key={r.key} className="intel-row" data-row={r.key} data-live={r.live ? '' : undefined}>
              <RowButton title={r.title} sub={`${r.sub} · ${r.summary}`} open={open === r.key} onToggle={() => toggle(r.key)}>
                <AgeStamp age={r.age} />
              </RowButton>
              {open === r.key ? (
                <div className="intel-detail">
                  {r.live ? null : <p className="intel-dim">{cfg.markets.loggedNote}</p>}
                  <WareTable cfg={cfg} row={r} />
                </div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  function Stations({ nav }: DashSectionProps): ReactElement {
    useTick(hz, isOpen)
    const root = useRef<HTMLDivElement>(null)
    useButtonNav(nav, root)
    const [open, toggle] = useOpenRow()
    const career = deps.career()
    if (career === null) return <Empty text={cfg.noCareer} />
    const m = stationsModel({
      ...base(career),
      here: deps.here(),
      dockedStationId: deps.dockedStationId(),
      seen: deps.seen(),
      known: deps.directory()?.stations ?? [],
      seenSource: { priceLog: career.priceLog, covered: new Set() },
    })
    if (m.rows.length === 0) return <Empty text={cfg.stations.empty} />
    return (
      <div ref={root} className="intel" data-intel="stations">
        <ul className="intel-list">
          {m.rows.map((r) => (
            <li key={r.key} className="intel-row" data-row={r.key} data-live={r.live ? '' : undefined}>
              <RowButton title={r.title} sub={r.systemName} open={open === r.key} onToggle={() => toggle(r.key)}>
                <AgeStamp age={r.age} />
              </RowButton>
              {open === r.key ? (
                <div className="intel-detail">{r.panel === null ? <p className="intel-dim">{cfg.stations.notRecorded}</p> : <StationPanel model={r.panel} />}</div>
              ) : null}
            </li>
          ))}
        </ul>
      </div>
    )
  }

  function Charts({ ctx, nav }: DashSectionProps): ReactElement {
    useTick(hz, isOpen)
    const root = useRef<HTMLDivElement>(null)
    useButtonNav(nav, root)
    const charts = deps.charts()
    if (charts === null) return <Empty text={cfg.noCareer} />
    const m = chartsModel({ cfg, unsold: charts.unsold, firstDiscoveries: charts.firstDiscoveries, firstMult: deps.firstMult, systemName: deps.systemName })
    if (m.rows.length === 0) return <Empty text={cfg.charts.empty} />
    const text = deps.dash.dockedAction
    const dir = deps.directory()
    const hit = dir === null ? null : dockedActionModel(text, dir, 'chart-buyer').hit
    return (
      <div ref={root} className="intel" data-intel="charts">
        <dl className="intel-sum">
          <dt>{cfg.charts.total}</dt>
          <dd className="intel-num" data-testid="intel-charts-total">
            {m.total} <span className="intel-dim">· {m.count}</span>
          </dd>
        </dl>
        <table className="intel-table intel-charts">
          <tbody>
            {m.rows.map((r) => (
              <tr key={r.systemId} data-row={r.systemId}>
                <td>{r.name}</td>
                <td className="intel-num">{r.value}</td>
                <td className="intel-good">{r.first ?? ''}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <p className="intel-dim">{cfg.charts.buyer}</p>
        {ctx.docked ? null : (
          <DockedAction
            cfg={text}
            label={cfg.charts.sell}
            reason={text.reasons['chart-buyer']}
            nearest={toNearestStation(text, hit)}
            onSetCourse={() => {
              if (dir !== null && hit !== null) deps.setCourse(dir, hit.station.id)
            }}
          />
        )}
      </div>
    )
  }

  const available = (): boolean => true
  return [
    { id: 'intel.markets', tab: 'intel', order: cfg.order.markets, label: cfg.sections.markets, available, component: Markets },
    { id: 'intel.stations', tab: 'intel', order: cfg.order.stations, label: cfg.sections.stations, available, component: Stations },
    { id: 'intel.charts', tab: 'intel', order: cfg.order.charts, label: cfg.sections.charts, available, component: Charts },
  ]
}
